import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Institution } from '@/api/services/institutions';
import { CustomCheckBox } from '@/components/SharedComponents/CustomCheckBox/';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { TextLink } from '@/components/TextLink';
import { Button } from '@/components/ui/therapist/Button';
import Dropdown from '@/components/ui/therapist/Dropdown';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { Links } from '@/config/links';
import { isValidEmail } from '@/utils/stringValidator';
import { containsNonAsciiCharacters } from '@/utils/containsNonAsciiCharacters';
import { IS_ANDROID } from '@/utils/platform';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { SignUpFormData } from './SignUpFormData';
import { AnonymousStackParamList } from '@/navigations/AnonymousStack';
import { styles } from './styles';

interface TherapistSignUpFormProps {
  control: Control<SignUpFormData>;
  handleSubmit: () => void;
  institutions: Institution[];
  isDataProcessingConsentConfirmed: boolean;
  setDataProcessingConsentConfirmation: (value: boolean) => void;
  isDataUsageConsentConfirmed: boolean;
  setDataUsageConsentConfirmation: (value: boolean) => void;
  isNewsletterConfirmed: boolean;
  setNewsletterConfirmation: (value: boolean) => void;
  showNewsletter: boolean;
  isSubmitted: boolean;
  filledPassword: string;
  errors: FieldErrors<SignUpFormData>;
}

export const TherapistSignUpForm: React.FC<TherapistSignUpFormProps> = ({
  control,
  handleSubmit,
  institutions,
  isDataProcessingConsentConfirmed,
  setDataProcessingConsentConfirmation,
  isDataUsageConsentConfirmed,
  setDataUsageConsentConfirmation,
  isNewsletterConfirmed,
  setNewsletterConfirmation,
  showNewsletter,
  isSubmitted,
  filledPassword,
  errors,
}) => {
  const { navigate } = useNavigation<NavigationProp<AnonymousStackParamList>>();
  const dataProcessingConsentText = (
    <Text>
      Ich willige ein, dass meine personenbezogenen Daten durch neolexon
      verarbeitet werden, um eine Sprachtherapie durchzuführen. Weitere
      Informationen zu meinen Rechten, der Speicherung und Verarbeitung der
      Daten finde ich in der{' '}
      <TextLink
        testID="therapist-registration-data-link"
        href={Links.datenschutz.href}
      >
        Datenschutzerklärung
      </TextLink>
      , die ich gelesen habe.
      <Text style={{ color: colors.red }}>*</Text>
    </Text>
  );

  const dataUsageConsentText = (
    <Text>
      Ich willige ein, dass neolexon meine pseudonymisierten Nutzungsdaten
      verarbeitet, um die Apps technisch stabil, nutzerfreundlich und
      zukunftsfähig zu gestalten.
    </Text>
  );
  return (
    <SafeAreaView
      style={[
        {
          backgroundColor: colors.newUI.therapist.bgBlue10,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 24,
        },
      ]}
    >
      <KeyboardAwareScrollView enableOnAndroid>
        <Text
          style={[
            styles.title,
            { marginVertical: 24, fontFamily: 'InterSemiBold' },
          ]}
        >
          Kostenlos registrieren und sofort starten
        </Text>
        <Text style={{ marginBottom: 12 }}>
          Mit <Text style={{ color: colors.red }}>*</Text> markierte Felder sind
          erforderlich
        </Text>
        <View
          style={[
            styles.formWrapper,
            {
              backgroundColor: colors.white,
              borderRadius: 12,
              width: '100%',
              maxWidth: 850,
            },
          ]}
        >
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="firstName"
                label="Vorname"
                testID="therapist-registration-first-name-input"
                rules={{ required: 'Vorname wird benötigt.' }}
                autoCapitalize="words"
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="lastName"
                label="Nachname"
                testID="therapist-registration-last-name-input"
                rules={{ required: 'Nachname wird benötigt.' }}
                autoCapitalize="words"
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <Controller
                name="institution"
                control={control}
                rules={{ required: 'Institution wird benötigt.' }}
                render={({ field }) => (
                  <Dropdown
                    testID="therapist-registration-institution"
                    statusMessage={errors.institution?.message}
                    onSelectChange={(value: string[]) =>
                      field.onChange(value[0])
                    }
                    status={errors.institution ? 'warning' : undefined}
                    required
                    data={institutions?.map((institution) => ({
                      label: institution.displayName,
                      value: institution.value,
                      testID: `therapist-registration-institution-option-${institution.value}`,
                    }))}
                    label="Institution"
                    value={field.value ? [field.value] : []}
                  />
                )}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="email"
                label="E-Mail-Adresse"
                testID="therapist-registration-email-input"
                keyboardType="email-address"
                rules={{
                  required: 'E-Mail-Adresse wird benötigt.',
                  validate: (value: string) => {
                    if (containsNonAsciiCharacters(value)) {
                      return 'E-Mail-Adresse enthält ungültige Zeichen';
                    }

                    if (!isValidEmail(value)) {
                      return 'E-Mail-Adresse ist ungültig';
                    }

                    return true;
                  },
                }}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="password"
                label="Passwort (mindestens 8 Zeichen)"
                testID="therapist-registration-password-input"
                secureTextEntry={true}
                rules={{
                  required: 'Passwort wird benötigt.',
                  minLength: {
                    value: 8,
                    message:
                      'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                  },
                  validate: (value: string) => passwordValidate(value),
                }}
              />
            </View>
          </View>
          <View style={styles.formField}>
            <View style={styles.therapistInputWrapper}>
              <SharedFormField
                control={control}
                name="confirmPassword"
                label="Wiederholung Passwort"
                testID="therapist-registration-confirm-password-input"
                secureTextEntry={true}
                rules={{
                  required: 'Passwortwiederholung wird benötigt.',
                  validate: (value: string) =>
                    confirmPasswordValidate(value, filledPassword),
                }}
              />
            </View>
          </View>
          <View style={{ marginHorizontal: 12 }}>
            <View style={styles.checkbox}>
              <CustomCheckBox
                testID="therapist-registration-privacy-consent-checkbox"
                onChecked={() =>
                  setDataProcessingConsentConfirmation(
                    !isDataProcessingConsentConfirmed,
                  )
                }
                checked={isDataProcessingConsentConfirmed}
                isLabelComponent={IS_ANDROID}
                label={
                  <PressableNeolexon
                    onPress={() =>
                      setDataProcessingConsentConfirmation(
                        !isDataProcessingConsentConfirmed,
                      )
                    }
                  >
                    <Text style={styles.checkBoxText}>
                      {dataProcessingConsentText}{' '}
                      {!isDataProcessingConsentConfirmed && isSubmitted ? (
                        <Text style={styles.mandatoryErrorCheck}>
                          (Pflichtfeld)
                        </Text>
                      ) : null}
                    </Text>
                  </PressableNeolexon>
                }
                disabled={false}
              />
            </View>
            <View style={styles.checkbox}>
              <CustomCheckBox
                checked={isDataUsageConsentConfirmed}
                onChecked={() => {
                  setDataUsageConsentConfirmation(!isDataUsageConsentConfirmed);
                }}
                isLabelComponent={IS_ANDROID}
                label={
                  <PressableNeolexon
                    onPress={() =>
                      setDataUsageConsentConfirmation(
                        !isDataUsageConsentConfirmed,
                      )
                    }
                  >
                    <Text style={styles.checkBoxText}>
                      {dataUsageConsentText}
                    </Text>
                  </PressableNeolexon>
                }
                disabled={false}
              />
            </View>
            <View style={styles.checkbox}>
              {showNewsletter ? (
                <CustomCheckBox
                  checked={isNewsletterConfirmed}
                  onChecked={() =>
                    setNewsletterConfirmation(!isNewsletterConfirmed)
                  }
                  isLabelComponent={IS_ANDROID}
                  label={
                    <View>
                      <PressableNeolexon
                        onPress={() =>
                          setNewsletterConfirmation(!isNewsletterConfirmed)
                        }
                      >
                        <Text style={styles.checkBoxText}>
                          Ich möchte den Newsletter erhalten.
                        </Text>
                      </PressableNeolexon>
                    </View>
                  }
                  disabled={false}
                />
              ) : null}
            </View>
            <Text style={{ margin: 12 }}>
              Ich kann meine Einwilligungen jederzeit widerrufen. Mit meiner
              Registrierung stimme ich den 
              <TextLink
                testID="therapist-registration-agb-link"
                href={Links.agb.href}
              >
                AGB
              </TextLink>
               zu.
            </Text>
          </View>
        </View>
        <View
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Button
            label="Zum Login"
            onPress={() => navigate('Login', {})}
            look="tertiary"
          />
          <Button
            testID="therapist-registration-submit"
            label="Registrieren"
            onPress={() => handleSubmit()}
            look="primary"
          />
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  );
};
